import React, { useEffect, useState, useCallback } from 'react';
import apiUrls from '../../../api/apiUrls';
import axiosInstance from '../../../api/axiosInstance';
import Sidebar from 'components/sidebar/Sidebar';
import {
  Box,
  Flex,
  Text,
  Switch,
  useColorModeValue,
  Heading,
  IconButton,
  useToast,
  HStack,
  Button,
  Checkbox,
} from '@chakra-ui/react';
import ComplexTable from '../default/components/ComplexTable';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import GlobalAlertDialog from 'components/GlobalAlertDialog';
import ViewMoreModal from 'components/ViewMoreModal';
import { useNavigate } from 'react-router-dom';
import BreadCrumb from 'components/breadCrumb/BreadCrumb';

const ModbusConfigs = () => {
  const userAuthority = localStorage.getItem('userAuthority');
  const isTenantAdmin = userAuthority === 'TENANT_ADMIN';

  const breadcrumbItems = [
    { label: 'Home', path: '/dashboard' },
    { label: 'All Configurations', path: '/modbus-configs' },
  ];

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [loadingDevices, setLoadingDevices] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowName, setSelectedRowName] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalDevices, setViewModalDevices] = useState([]);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [toggleRowId, setToggleRowId] = useState(null);
  const [toggleRowActive, setToggleRowActive] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [serialNumber, setSerialNumber] = useState('');
  const [pageNumber, setPageNumber] = useState(0);

  // Pagination states (similar to DeviceContext)
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalConfigs, setTotalConfigs] = useState(0);

  const platformId = process.env.REACT_APP_PLATFORM_ID;
  const sidebarExpandedWidth = '240px';
  const sidebarCollapsedWidth = '60px';
  const sidebarWidth = collapsed ? sidebarCollapsedWidth : sidebarExpandedWidth;
  const toast = useToast();
  const navigate = useNavigate();
  const groupID = localStorage.getItem('groupID');

  const handleEdit = (rowId, devices, status, isActive) => {
    localStorage.setItem('configId', rowId);
    console.log('Navigating to edit with:', {
      rowId,
      devices,
      status,
      isActive,
    });
    navigate(`/slave-configurations/${rowId}`, {
      state: { fromModbusConfigs: true, devices, status, isActive },
    });
  };

  const handleStatusChange = (value) => {
    setSelectedStatus((prev) => {
      const newStatus = prev === value ? 'all' : value;

      fetchAllConfigs(
        serialNumber,
        false,
        0,
        newStatus === 'all' ? '' : newStatus,
      );

      return newStatus;
    });
  };
  const fetchAllConfigs = useCallback(
    async (searchBy = '', _ = false, page = 0, filterBy = '') => {
      const pageNumber = typeof page === 'number' && page >= 0 ? page + 1 : 1;

      setLoadingDevices(true);

      try {
        const response = await axiosInstance.get(
          apiUrls.fetchAllModbusConfigs(
            platformId,
            groupID,
            filterBy,
            searchBy,
            pageNumber, // ✅ correct
          ),
        );

        const configs = response.data.results || [];

        const mappedData = configs
          .map((item) => ({
            name: item.name,
            devices: item.devices || [],
            updatedAt: item.updated_at,
            id: item.id,
            isActive: item.is_active,
            status: item.status?.toLowerCase() || '',
          }))
          .sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, {
              sensitivity: 'base',
            }),
          );
        setTableData(mappedData);
        setHasNext(!!response.data.next);
        setTotalPages(Math.ceil(response.data.count / 10)); // Assuming page size is 10
        setTotalConfigs(response.data.count);
      } catch (error) {
        console.error('Error fetching Modbus Configs:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch configurations',
          status: 'error',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
      } finally {
        setLoadingDevices(false);
      }
    },
    [platformId, groupID, toast],
  );

  // Initial fetch
  useEffect(() => {
    fetchAllConfigs();
  }, [fetchAllConfigs]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  // Delete handler
  const handleDelete = (rowId) => {
    setSelectedRowId(rowId);
    const row = tableData.find((row) => row.id === rowId);
    setSelectedRowName(row ? row.name : null);
    setAlertOpen(true);
  };

  const handleToggleClick = (rowId, isActive) => {
    setToggleRowId(rowId);
    setToggleRowActive(isActive);
    setToggleDialogOpen(true);
  };

  const confirmToggle = async () => {
    const configRow = tableData.find((row) => row.id === toggleRowId);
    if (!configRow) return;

    const newIsActive = !toggleRowActive;
    try {
      await axiosInstance.post(
        apiUrls.setConfigStatus(platformId, groupID, toggleRowId),
        { is_active: newIsActive },
      );

      // Refresh the current page data after toggle
      fetchAllConfigs();

      toast({
        title: 'Config status updated',
        status: 'success',
        duration: 2000,
        position: 'top',
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Failed to update config status',
        description: err.response?.data?.error || 'Something went wrong',
        status: 'error',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setToggleDialogOpen(false);
      setToggleRowId(null);
      setToggleRowActive(null);
    }
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);

    try {
      await axiosInstance.delete(apiUrls.deleteModbusConfig(selectedRowId));

      // Refresh the current page data after delete
      fetchAllConfigs();

      toast({
        title: 'Modbus Config Deleted',
        description: 'Modbus configuration deleted successfully.',
        status: 'success',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    } catch (err) {
      const statusCode = err.response?.status;
      const message = err.response?.data?.error || 'Something went wrong';

      toast({
        title: 'Info',
        description: message,
        status: statusCode === 409 ? 'info' : 'error',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
      setAlertOpen(false);
      setSelectedRowId(null);
      setSelectedRowName(null);
    }
  };

  const columnsData = [
    {
      header: 'Sno',
      accessorKey: 'sno',
      id: 'sno',
      cell: (info) => pageNumber * 10 + info.row.index + 1,
    },
    {
      header: 'Name',
      accessorKey: 'name',
      id: 'name',
      cell: (info) => info.getValue(),
    },
    {
      header: 'Devices',
      accessorKey: 'devices',
      id: 'devices',
      cell: (info) => {
        const devices = info.getValue();
        if (!devices || devices.length === 0)
          return <Text color="gray.400">-</Text>;
        return (
          <Flex align="center">
            <Text fontWeight="bold">{devices.length}</Text>
          </Flex>
        );
      },
    },
    {
      header: 'Status',
      accessorKey: 'status',
      id: 'status',
      cell: (info) => {
        const status = info.getValue();
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : '-';
      },
    },
    {
      header: 'Updated At',
      accessorKey: 'updatedAt',
      id: 'updatedAt',
      cell: (info) => info.getValue(),
    },
    {
      header: 'Active',
      accessorKey: 'state',
      id: 'state',
      cell: (info) => (
        <span
          style={{
            cursor: isTenantAdmin ? 'pointer' : 'not-allowed',
            opacity: isTenantAdmin ? 1 : 0.6,
          }}
          onClick={() => {
            if (isTenantAdmin) {
              handleToggleClick(
                info.row.original.id,
                info.row.original.isActive,
              );
            }
          }}
        >
          <Switch
            isChecked={info.row.original.isActive}
            colorScheme="green"
            size="lg"
            pointerEvents="none"
            isDisabled={!isTenantAdmin}
          />
        </span>
      ),
    },
    {
      header: 'Action',
      accessorKey: 'action',
      id: 'action',
      cell: (info) => (
        <HStack spacing={2}>
          <IconButton
            aria-label="Edit configuration"
            icon={<EditIcon />}
            size="sm"
            colorScheme="blue"
            onClick={() =>
              handleEdit(
                info.row.original.id,
                info.row.original.devices,
                info.row.original.status,
                info.row.original.isActive,
              )
            }
          />

          {isTenantAdmin && (
            <IconButton
              aria-label="Delete configuration"
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              onClick={() => handleDelete(info.row.original.id)}
            />
          )}
        </HStack>
      ),
    },
  ];

  // Apply status filter to the data
  const filteredTableData =
    selectedStatus === 'all'
      ? tableData
      : tableData.filter((row) => row.status?.toLowerCase() === selectedStatus);

  return (
    <Box
      ml={sidebarWidth}
      transition="margin-left 0.3s ease"
      p={6}
      ms={0}
      overflowX={'hidden'}
    >
      <BreadCrumb items={breadcrumbItems} sidebarWidth={sidebarWidth} />
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <Flex
        gap={4}
        alignItems="center"
        justifyContent="space-between"
        mb="4"
        w="100%"
      >
        <Heading size="lg" mb={6} ml={sidebarWidth}>
          Modbus Configurations
        </Heading>
        <Flex align="center" gap={6}>
          <Flex align="center" gap={2}>
            <Checkbox
              isChecked={selectedStatus === 'all'}
              onChange={() => {
                setSelectedStatus('all');
                fetchAllConfigs(serialNumber, false, 0, '');
              }}
            >
              All ({totalConfigs})
            </Checkbox>
            <Checkbox
              isChecked={selectedStatus === 'draft'}
              onChange={() => handleStatusChange('draft')}
            >
              Draft
            </Checkbox>
            <Checkbox
              isChecked={selectedStatus === 'published'}
              onChange={() => handleStatusChange('published')}
            >
              Published
            </Checkbox>
          </Flex>
          <Flex align="center" gap={3}>
            {isTenantAdmin && (
              <Button
                size="sm"
                colorScheme="green"
                variant="outline"
                border="2px"
                cursor="pointer"
                onClick={() => {
                  navigate('/add-configuration');
                }}
              >
                Add New Configuration
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>

      <Box ml={sidebarWidth} mb={20}>
        <ComplexTable
          title="Modbus Configs"
          columnsData={columnsData}
          tableData={tableData}
          loading={loadingDevices}
          fetchAllDevices={(searchBy, _, page) => {
            setPageNumber(page);
            return fetchAllConfigs(
              searchBy,
              false,
              page,
              selectedStatus === 'all' ? '' : selectedStatus,
            );
          }}
          hasNext={hasNext}
          totalPages={totalPages}
          pageSize={10}
          enableLocalPagination={false}
          showSearchBar={true}
        />
      </Box>

      <GlobalAlertDialog
        isOpen={alertOpen}
        onClose={() => {
          setAlertOpen(false);
          setSelectedRowName(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Confirmation"
        body={
          selectedRowName
            ? `Are you sure you want to delete the Modbus Config "${selectedRowName}"?`
            : 'Are you sure you want to delete this Modbus Config?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        showCancel={true}
        isLoading={deleteLoading}
      />

      <ViewMoreModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        items={viewModalDevices}
        title="Devices"
        pageSize={16}
      />

      <GlobalAlertDialog
        isOpen={toggleDialogOpen}
        onClose={() => setToggleDialogOpen(false)}
        onConfirm={confirmToggle}
        title={
          toggleRowActive ? 'Deactivate Confirmation' : 'Activate Confirmation'
        }
        body={
          toggleRowActive
            ? 'Are you sure you want to deactivate this configuration?'
            : 'Are you sure you want to activate this configuration?'
        }
        confirmText={toggleRowActive ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        showCancel={true}
      />
      <Flex
        as="footer"
        position="fixed"
        left={0}
        bottom="0"
        width="100%"
        bg={useColorModeValue('gray.100', 'navy.800')}
        color={useColorModeValue('gray.600', 'gray.200')}
        py={2}
        px={4}
        align="center"
        justify="space-between"
        zIndex={20} // higher than sidebar
        borderTop="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
      >
        <Text fontSize="sm" fontWeight="medium">
          Version 1.0.0
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          UGX Systems
        </Text>
      </Flex>
    </Box>
  );
};

export default ModbusConfigs;
